import * as Sentry from '@sentry/react';

import alertChimeUrl from '../assets/sounds/alert-chime.wav';
import {
  ALERT_SOUND_REPEAT_COUNT,
  ALERT_SOUND_REPEAT_INTERVAL_MS,
  ALERT_SOUND_THROTTLE_MS,
} from '../constants/alert-sound.js';
import { useAlertSoundStore } from '../store/alertSound.js';

// Chimes of one burst fail together on a broken asset, so the burst reports the first failure only.
type Burst = { failureReported: boolean };

let lastAttemptAt: number | null = null;
let pendingRepeats: ReturnType<typeof setTimeout>[] = [];
const playingChimes = new Set<HTMLAudioElement>();
const announcedAlertIds = new Set<string>();
let burstEndsAt: number | null = null;
let unlockProbe: HTMLAudioElement | null = null;

const isThrottled = (now: number): boolean =>
  lastAttemptAt !== null && now - lastAttemptAt < ALERT_SOUND_THROTTLE_MS;

// The throttle outlives a burst that a failed play ended, so it cannot say whether one is audible.
const isBurstAudible = (now: number): boolean => burstEndsAt !== null && now < burstEndsAt;

const isAutoplayRejection = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'NotAllowedError';

const isAbortRejection = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

const endBurst = (): void => {
  pendingRepeats.forEach(clearTimeout);
  pendingRepeats = [];
  playingChimes.forEach((chime) => chime.pause());
  playingChimes.clear();
  announcedAlertIds.clear();
  burstEndsAt = null;
};

export const stopAlertSound = (): void => {
  endBurst();
  lastAttemptAt = null;
};

// Acknowledging one alert must not silence a burst that also announces another, still-unacked one.
export const stopAlertSoundForAlert = (alertId: string): void => {
  if (!isBurstAudible(Date.now())) {
    return;
  }
  if (announcedAlertIds.delete(alertId) && announcedAlertIds.size === 0) {
    stopAlertSound();
  }
};

const reportBurstFailure = (burst: Burst, error: unknown): void => {
  if (!burst.failureReported) {
    burst.failureReported = true;
    Sentry.captureException(error);
  }
};

// A chime no longer in playingChimes was stopped or superseded: its outcome says nothing about the
// page's current autoplay state, and pausing it rejects its pending play() with an AbortError.
const handleDroppedChimeRejection = (burst: Burst, error: unknown): void => {
  if (!isAutoplayRejection(error) && !isAbortRejection(error)) {
    reportBurstFailure(burst, error);
  }
};

const handlePlayRejection = (chime: HTMLAudioElement, burst: Burst, error: unknown): void => {
  if (!playingChimes.delete(chime)) {
    handleDroppedChimeRejection(burst, error);
    return;
  }
  // A missing or undecodable asset fails every repeat the same way, so any rejection ends the burst.
  endBurst();
  // Autoplay policy rejects play() until the page has had a user gesture: expected, not an error.
  if (isAutoplayRejection(error)) {
    lastAttemptAt = null;
    useAlertSoundStore.getState().setBlocked(true);
    return;
  }
  // The throttle stays armed, so a broken asset is reported at most once per window.
  reportBurstFailure(burst, error);
};

const handlePlaySuccess = (chime: HTMLAudioElement): void => {
  if (playingChimes.has(chime)) {
    useAlertSoundStore.getState().setBlocked(false);
  }
};

const startChime = (chime: HTMLAudioElement, burst: Burst): void => {
  playingChimes.add(chime);
  chime.addEventListener('ended', () => playingChimes.delete(chime), { once: true });
  chime.play().then(
    () => handlePlaySuccess(chime),
    (error: unknown) => handlePlayRejection(chime, burst, error),
  );
};

// Every play gets its own element: the chime outlasts the repeat interval, and calling play() on an
// element that is already playing does not restart it.
const playChime = (burst: Burst): void => startChime(new Audio(alertChimeUrl), burst);

const scheduleRepeats = (burst: Burst): void => {
  pendingRepeats = Array.from({ length: ALERT_SOUND_REPEAT_COUNT - 1 }, (_, index) =>
    setTimeout(() => playChime(burst), (index + 1) * ALERT_SOUND_REPEAT_INTERVAL_MS),
  );
};

const announceAlerts = (alertIds: readonly string[]): void => {
  alertIds.forEach((alertId) => announcedAlertIds.add(alertId));
};

export const playAlertSound = (alertIds: readonly string[]): void => {
  if (!useAlertSoundStore.getState().enabled) {
    return;
  }

  const now = Date.now();
  // The running burst absorbs this request, so it announces these alerts too.
  if (isThrottled(now)) {
    if (isBurstAudible(now)) {
      announceAlerts(alertIds);
    }
    return;
  }
  // Chime startup is not bounded by the throttle, so a late settlement of the previous burst must
  // find its chime already dropped rather than end this one.
  endBurst();
  lastAttemptAt = now;
  burstEndsAt = now + ALERT_SOUND_THROTTLE_MS;
  announceAlerts(alertIds);
  const burst: Burst = { failureReported: false };
  scheduleRepeats(burst);
  playChime(burst);
};

// Silenced by volume, not muted: autoplay policy lets muted playback through without a gesture, so
// its success would not prove the page is unlocked.
export const unlockAlertSound = (): void => {
  if (unlockProbe && playingChimes.delete(unlockProbe)) {
    unlockProbe.pause();
  }
  unlockProbe = new Audio(alertChimeUrl);
  unlockProbe.volume = 0;
  startChime(unlockProbe, { failureReported: false });
};

// The store cannot import the player (the player imports the store), so the player watches for the
// sound being turned off.
useAlertSoundStore.subscribe((state, previousState) => {
  if (previousState.enabled && !state.enabled) {
    stopAlertSound();
  }
});
